// unmanaged
//
// Evaluation tab on a report row. Shows:
//   * The envelope status pill + a dropdown for Admin/InternalUser to
//     change it. Status changes auto-log a status_change entry (server
//     side), so the log carries the audit trail.
//   * Every entry (bio_interpreter / status_change), most-recent
//     first — the chronological dialog history.
//
// Read/write gated to Admin OR InternalUser at the router; this component
// is only mounted for those roles by ReportTableBody.
import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  MenuItem,
  Rating,
  Select,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import UserAvatar from "./components/UserAvatar";
import { useEvaluationStore } from "./store/evaluationStore";
import { useAuthStore } from "./store/authStore";

const STATUS_LABEL = { open: "Open", in_progress: "In Progress", closed: "Closed" };
const STATUS_COLOR = { open: "info", in_progress: "warning", closed: "success" };
const STATUS_OPTIONS = ["open", "in_progress", "closed"];

function fmtWhen(ms) {
  if (!ms) return "—";
  return new Date(Number(ms)).toLocaleString();
}

function StatusPill({ status }) {
  const s = (status || "open").toLowerCase();
  return (
    <Chip
      size="small"
      label={STATUS_LABEL[s] || status || "Open"}
      color={STATUS_COLOR[s] || "default"}
      variant={s === "closed" ? "filled" : "outlined"}
    />
  );
}

// The 4 sub-criteria shown alongside the overall rating badge — "rating"
// (Overall Ranking) already gets its own stars + FAIL/PASS chip above.
const SUB_SCORE_ITEMS = [
  { key: "data_recalled_accurately", label: "Data Recalled" },
  { key: "summary_and_conclusions", label: "Summary" },
  { key: "scientific_discussion_and_interpretation", label: "Discussion" },
  { key: "citations", label: "Citations" },
];

// Renders one entry in the chat stream. Prefix depends on kind; bio
// entries also render the star rating badge.
function EntryRow({ entry, canDelete, onDelete }) {
  const kind = (entry.kind || "comment").toLowerCase();
  const isBI = kind === "bio_interpreter";
  const isStatus = kind === "status_change";
  const prefix = isBI
    ? "BioInterpreter Evaluation"
    : isStatus
      ? "Status change"
      : "Comment";
  const badge = isBI ? (
    <Chip
      size="small"
      label={entry.rating === 1 ? "FAIL" : entry.rating === 5 ? "PASS" : `${entry.rating || "?"}/5`}
      color={entry.rating === 1 ? "error" : entry.rating === 5 ? "success" : "default"}
      sx={{ ml: 1 }}
    />
  ) : null;
  const subScoreText = SUB_SCORE_ITEMS
    .filter(({ key }) => entry[key] != null)
    .map(({ key, label }) => `${label}: ${entry[key]}`)
    .join(" · ");

  return (
    <Box
      sx={{
        display: "flex",
        gap: 1.25,
        py: 1.25,
        borderTop: "1px solid #E2E8F0",
        "&:first-of-type": { borderTop: "none" },
      }}
    >
      <Box sx={{ pt: 0.25 }}>
        <UserAvatar email={entry.user_id} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, color: "#334155" }}>
            {entry.user_id || "—"}
          </Typography>
          <Chip
            size="small"
            variant="outlined"
            label={prefix}
            color={isBI ? "primary" : isStatus ? "warning" : "default"}
            sx={{ height: 20, fontSize: "0.68rem" }}
          />
          {isBI && entry.rating != null && (
            <Rating value={entry.rating} max={5} readOnly size="small" />
          )}
          {badge}
          <Typography variant="caption" sx={{ color: "#94A3B8" }}>
            {fmtWhen(entry.created)}
          </Typography>
          {entry.modified && entry.modified !== entry.created && (
            <Typography variant="caption" sx={{ color: "#94A3B8", fontStyle: "italic" }}>
              (edited {fmtWhen(entry.modified)})
            </Typography>
          )}
          <Box sx={{ flex: 1 }} />
          {canDelete && !isStatus && (
            <Tooltip title="Delete entry">
              <IconButton size="small" onClick={onDelete} sx={{ color: "#94A3B8" }}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
        {isBI && subScoreText && (
          <Typography variant="caption" sx={{ display: "block", color: "#64748B", mb: 0.5 }}>
            {subScoreText}
          </Typography>
        )}
        <Typography
          variant="body2"
          sx={{
            color: "#475569",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          {entry.comment || "—"}
        </Typography>
      </Box>
    </Box>
  );
}

export default function EvaluationChatPanel({ record, variant }) {
  const reportId = record?.id;

  const fetchForReport = useEvaluationStore((s) => s.fetchForReport);
  const cached = useEvaluationStore((s) => s.byReport[`${reportId}:${variant || 'default'}`]);
  const setStatus = useEvaluationStore((s) => s.setStatus);
  const deleteEntry = useEvaluationStore((s) => s.deleteEntry);

  const authUser = useAuthStore((s) => s.user);
  const userRoles = Array.isArray(authUser?.roles) ? authUser.roles : authUser?.roles ? [authUser.roles] : [];
  const isAdmin = userRoles.includes("Admin");

  const [loading, setLoading] = useState(!cached);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!reportId) return;
      setLoading(true);
      await fetchForReport(reportId, variant, false);
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [reportId, variant, fetchForReport]);

  const envelope = cached?.envelope || null;
  const entries = cached?.entries || [];
  const status = envelope?.status || "open";

  const rated = useMemo(
    () => entries.filter((e) => (e.kind || "").toLowerCase() === "bio_interpreter" && e.rating != null),
    [entries]
  );
  const avgRating = rated.length
    ? (rated.reduce((s, e) => s + Number(e.rating || 0), 0) / rated.length)
    : null;

  const handleStatusChange = async (e) => {
    const next = e.target.value;
    if (!next || next === status) return;
    setError(null);
    try {
      await setStatus(reportId, variant, next);
      await fetchForReport(reportId, variant, true);
    } catch (err) {
      setError(err?.message || "Failed to update status");
    }
  };

  const handleDelete = async (entry) => {
    try {
      await deleteEntry(entry.id, reportId, variant);
      await fetchForReport(reportId, variant, true);
    } catch (e) {
      setError(e?.message || "Failed to delete entry");
    }
  };

  if (loading && entries.length === 0) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <Typography variant="subtitle2" sx={{ color: "#334155" }}>
          Evaluation
        </Typography>
        <StatusPill status={status} />
        <Select
          size="small"
          value={status}
          onChange={handleStatusChange}
          sx={{ ml: 1, height: 32, minWidth: 140 }}
        >
          {STATUS_OPTIONS.map((s) => (
            <MenuItem key={s} value={s}>{STATUS_LABEL[s]}</MenuItem>
          ))}
        </Select>
        <Box sx={{ flex: 1 }} />
        <Typography variant="caption" sx={{ color: "#64748B" }}>
          {entries.length} {entries.length === 1 ? "entry" : "entries"}
          {avgRating != null && (
            <> · avg <Rating value={avgRating} max={5} readOnly precision={0.1} size="small" sx={{ verticalAlign: "middle" }} /></>
          )}
        </Typography>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {entries.length === 0 ? (
        <Typography variant="body2" sx={{ color: "text.secondary", py: 3, textAlign: "center" }}>
          No entries yet. Submit a BioInterpreter evaluation below.
        </Typography>
      ) : (
        <Box>
          {entries.map((entry) => (
            <EntryRow
              key={entry.id}
              entry={entry}
              canDelete={isAdmin || entry.user_id === authUser?.id}
              onDelete={() => handleDelete(entry)}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}
